/**
 * components/select.tsx — the design's `select.t-input` and the builder inline
 * token `.bld-tok`. On web we render a real <select> (crisp native caret +
 * accessibility, exactly like the design); on native a Pressable opens a modal
 * option list. One component, two render paths, zero duplication.
 */
import React, { useState } from 'react';
import { Platform, Pressable, View, Modal, ScrollView, StyleProp, ViewStyle } from 'react-native';
import { LightColors as C, Radii as R, FontSize as FS, FontFamily as FF, FontWeight as FW } from '../theme/theme';
import { Txt } from './ui';

export interface Option {
  value: string;
  label: string;
  disabled?: boolean;
}

export type SelectVariant = 'input' | 'token';

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  variant?: SelectVariant;
  sm?: boolean;
  ariaLabel?: string;
  style?: StyleProp<ViewStyle>;
  /** Web-only inline style escape hatch (e.g. height tweak in the sidebar). */
  webStyle?: React.CSSProperties;
}

// Caret rendered the theme.css way: two tiny gradient triangles.
function caret(color: string, token: boolean): React.CSSProperties {
  return {
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `linear-gradient(45deg, transparent 50%, ${color} 50%), linear-gradient(135deg, ${color} 50%, transparent 50%)`,
    backgroundPosition: token
      ? 'calc(100% - 11px) 56%, calc(100% - 7px) 56%'
      : 'calc(100% - 14px) 50%, calc(100% - 9px) 50%',
    backgroundSize: token ? '4px 4px, 4px 4px' : '5px 5px, 5px 5px',
    backgroundRepeat: 'no-repeat',
  };
}

const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  variant = 'input',
  sm = false,
  ariaLabel,
  style,
  webStyle,
}) => {
  const [open, setOpen] = useState(false);
  const token = variant === 'token';
  const current = options.find((o) => o.value === value);

  if (Platform.OS === 'web') {
    const base: React.CSSProperties = token
      ? {
          border: 'none',
          borderBottom: `2px solid ${C.accentLine}`,
          background: C.accentSoft,
          color: C.accentInk,
          fontWeight: 600,
          fontSize: 15,
          fontFamily: `${FF.sans}, sans-serif`,
          padding: '3px 24px 3px 9px',
          borderRadius: 6,
          cursor: 'pointer',
          lineHeight: 1.3,
          ...caret(C.accent, true),
        }
      : {
          width: '100%',
          height: sm ? 28 : 34,
          border: `1px solid ${C.line}`,
          background: C.card,
          color: C.ink,
          fontSize: sm ? FS.sm : FS.md,
          fontFamily: `${FF.sans}, sans-serif`,
          fontWeight: sm ? 500 : 400,
          padding: sm ? '0 28px 0 10px' : '0 28px 0 12px',
          borderRadius: R.r2,
          cursor: 'pointer',
          ...caret(C.ink3, false),
        };

    return React.createElement(
      'select',
      {
        value,
        'aria-label': ariaLabel,
        onChange: (e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value),
        style: { ...base, ...webStyle },
      },
      options.map((o) =>
        React.createElement('option', { key: o.value, value: o.value, disabled: o.disabled }, o.label),
      ),
    );
  }

  // ── Native: pressable + modal option list ────────────────────────────────
  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        style={[
          token
            ? {
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                backgroundColor: C.accentSoft,
                borderBottomWidth: 2,
                borderBottomColor: C.accentLine,
                borderRadius: 6,
                paddingVertical: 3,
                paddingHorizontal: 9,
              }
            : {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: sm ? 28 : 34,
                borderWidth: 1,
                borderColor: C.line,
                backgroundColor: C.card,
                borderRadius: R.r2,
                paddingHorizontal: sm ? 10 : 12,
              },
          style,
        ]}
      >
        <Txt
          size={token ? 15 : sm ? FS.sm : FS.md}
          weight={token ? FW.semibold : FW.regular}
          color={token ? C.accentInk : C.ink}
        >
          {current?.label ?? ''}
        </Txt>
        {!token && (
          <Txt size={10} color={C.ink3}>
            ▾
          </Txt>
        )}
      </Pressable>
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable
          onPress={() => setOpen(false)}
          style={{ flex: 1, backgroundColor: 'rgba(16,24,40,0.42)', justifyContent: 'center', padding: 24 }}
        >
          <View style={{ backgroundColor: C.card, borderRadius: R.r4, maxHeight: '70%', overflow: 'hidden' }}>
            <ScrollView>
              {options.map((o) => (
                <Pressable
                  key={o.value}
                  disabled={o.disabled}
                  onPress={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  style={{
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                    borderBottomWidth: 1,
                    borderBottomColor: C.line,
                    opacity: o.disabled ? 0.5 : 1,
                    backgroundColor: o.value === value ? C.paper2 : C.card,
                  }}
                >
                  <Txt color={C.ink}>{o.label}</Txt>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        </Pressable>
      </Modal>
    </>
  );
};

export default Select;
